import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import api from "@/api";
import { formatDate, formatTimestamp } from "@/utils/timezone";
import { formatCurrency } from "@/utils/formatters";

const ActivityTable = ({ accountId, refreshTrigger = 0 }) => {
  const [transactions, setTransactions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Pagination state
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);
  const [limit] = useState(50);

  // Filter state
  const [actionFilter, setActionFilter] = useState("ALL");
  const [tickerFilter, setTickerFilter] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");

  // Sort state
  const [sortField, setSortField] = useState("date");
  const [sortDirection, setSortDirection] = useState("desc");

  // Reset to page 1 when account changes or refresh is triggered
  useEffect(() => {
    setPage(1);
  }, [accountId, refreshTrigger]);

  // Fetch transactions
  useEffect(() => {
    const fetchTransactions = async () => {
      setLoading(true);
      setError(null);

      try {
        const params = new URLSearchParams({
          page: page.toString(),
          limit: limit.toString(),
          sort_field: sortField,
          sort_direction: sortDirection,
        });

        if (actionFilter && actionFilter !== "ALL") {
          params.append("action", actionFilter);
        }
        if (tickerFilter) {
          params.append("ticker", tickerFilter);
        }
        if (startDate) {
          params.append("start_date", startDate);
        }
        if (endDate) {
          params.append("end_date", endDate);
        }

        const url = accountId
          ? `/accounts/${accountId}/transactions?${params}`
          : `/accounts/all/transactions?${params}`;

        const response = await api.get(url);

        setTransactions(response.data.transactions || []);
        setTotal(response.data.total || 0);
        setTotalPages(response.data.total_pages || 1);
      } catch (err) {
        console.error("Error fetching transactions:", err);
        setError("Failed to load transactions");
      } finally {
        setLoading(false);
      }
    };

    fetchTransactions();
  }, [
    accountId,
    page,
    limit,
    actionFilter,
    tickerFilter,
    startDate,
    endDate,
    sortField,
    sortDirection,
    refreshTrigger,
  ]);

  const handleSort = (field) => {
    if (sortField === field) {
      // Toggle direction
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // New field, default to descending
      setSortField(field);
      setSortDirection("desc");
    }
    // Reset to page 1 when sorting changes
    setPage(1);
  };

  const resetFilters = () => {
    setActionFilter("ALL");
    setTickerFilter("");
    setStartDate("");
    setEndDate("");
    setPage(1);
  };

  const handleActionClick = (action) => {
    setActionFilter(action);
    setPage(1);
  };

  const handleDateClick = (dateString) => {
    // Set both start and end date to the same date to filter for just that day
    setStartDate(dateString);
    setEndDate(dateString);
    setPage(1);
  };

  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return (
        <svg
          className="w-4 h-4 opacity-30"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
          />
        </svg>
      );
    }

    return sortDirection === "asc" ? (
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M5 15l7-7 7 7"
        />
      </svg>
    ) : (
      <svg
        className="w-4 h-4"
        fill="none"
        viewBox="0 0 24 24"
        stroke="currentColor"
      >
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M19 9l-7 7-7-7"
        />
      </svg>
    );
  };

  if (loading && transactions.length === 0) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-600 dark:text-red-400">{error}</p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        {/* Action Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Action
          </label>
          <select
            value={actionFilter}
            onChange={(e) => {
              setActionFilter(e.target.value);
              setPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          >
            <option value="ALL">All Actions</option>
            <option value="BUY">Buy</option>
            <option value="SELL">Sell</option>
            <option value="DIVIDEND">Dividend</option>
            <option value="CONTRIBUTION">Contribution</option>
            <option value="CASH_IN">Cash In</option>
            <option value="SHARES_IN">Shares In</option>
            <option value="WITHDRAWAL">Withdrawal</option>
          </select>
        </div>

        {/* Ticker Filter */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Ticker
          </label>
          <input
            type="text"
            value={tickerFilter}
            onChange={(e) => {
              setTickerFilter(e.target.value);
              setPage(1);
            }}
            placeholder="e.g., AAPL"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500"
          />
        </div>

        {/* Date Range Filters */}
        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Start Date
          </label>
          <input
            type="date"
            value={startDate}
            onChange={(e) => {
              setStartDate(e.target.value);
              setPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>

        <div>
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            End Date
          </label>
          <input
            type="date"
            value={endDate}
            onChange={(e) => {
              setEndDate(e.target.value);
              setPage(1);
            }}
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-md bg-white dark:bg-gray-800 text-gray-900 dark:text-white"
          />
        </div>
      </div>

      {/* Reset Filters Button */}
      {(actionFilter !== "ALL" || tickerFilter || startDate || endDate) && (
        <div className="flex justify-end">
          <button
            onClick={resetFilters}
            className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 hover:text-gray-900 dark:hover:text-white"
          >
            Reset Filters
          </button>
        </div>
      )}

      {/* Results Count */}
      <div className="text-sm text-gray-600 dark:text-gray-400">
        Showing {transactions.length} of {total} transactions
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-800">
            <tr>
              <th
                onClick={() => handleSort("date")}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center gap-1">
                  Date
                  <SortIcon field="date" />
                </div>
              </th>
              <th
                onClick={() => handleSort("action")}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center gap-1">
                  Action
                  <SortIcon field="action" />
                </div>
              </th>
              <th
                onClick={() => handleSort("ticker")}
                className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center gap-1">
                  Ticker
                  <SortIcon field="ticker" />
                </div>
              </th>
              {accountId === null && (
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider">
                  Account
                </th>
              )}
              <th
                onClick={() => handleSort("shares")}
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center justify-end gap-1">
                  Shares
                  <SortIcon field="shares" />
                </div>
              </th>
              <th
                onClick={() => handleSort("price")}
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center justify-end gap-1">
                  Price
                  <SortIcon field="price" />
                </div>
              </th>
              <th
                onClick={() => handleSort("amount")}
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center justify-end gap-1">
                  Amount
                  <SortIcon field="amount" />
                </div>
              </th>
              <th
                onClick={() => handleSort("realized_pnl")}
                className="px-4 py-3 text-right text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider cursor-pointer hover:bg-gray-100 dark:hover:bg-gray-700"
              >
                <div className="flex items-center justify-end gap-1">
                  P&L
                  <SortIcon field="realized_pnl" />
                </div>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
            {transactions.length === 0 ? (
              <tr>
                <td
                  colSpan={accountId === null ? "9" : "8"}
                  className="px-4 py-8 text-center text-gray-500 dark:text-gray-400"
                >
                  No transactions found
                </td>
              </tr>
            ) : (
              transactions.map((transaction, idx) => (
                <tr
                  key={transaction.id || idx}
                  className="hover:bg-gray-50 dark:hover:bg-gray-800"
                >
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleDateClick(transaction.date)}
                      className="text-gray-900 dark:text-white hover:text-blue-600 dark:hover:text-blue-400 hover:underline cursor-pointer transition-colors"
                      title="Filter by this date"
                    >
                      {transaction.datetime
                        ? formatTimestamp(transaction.datetime, "MMM d, yyyy h:mm a")
                        : formatDate(transaction.date)}
                    </button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm">
                    <button
                      onClick={() => handleActionClick(transaction.action)}
                      title="Filter by this action"
                    >
                      <span
                        className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full cursor-pointer transition-opacity hover:opacity-75 ${
                          transaction.action === "BUY"
                            ? "bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200"
                            : transaction.action === "SELL"
                              ? "bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200"
                              : transaction.action === "DIVIDEND"
                                ? "bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200"
                                : "bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200"
                        }`}
                      >
                        {transaction.action}
                      </span>
                    </button>
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm font-medium text-gray-900 dark:text-white">
                    {transaction.ticker ? (
                      <Link
                        to={accountId
                          ? `/ticker/${transaction.ticker}?accountId=${accountId}`
                          : transaction.account_id
                            ? `/ticker/${transaction.ticker}?accountId=${transaction.account_id}`
                            : `/ticker/${transaction.ticker}`}
                        className="hover:underline cursor-pointer"
                      >
                        {transaction.ticker}
                      </Link>
                    ) : (
                      "-"
                    )}
                  </td>
                  {accountId === null && (
                    <td className="px-4 py-3 whitespace-nowrap text-sm text-gray-500 dark:text-gray-400">
                      {transaction.account_name || "-"}
                    </td>
                  )}
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    {transaction.shares ? transaction.shares : "-"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right text-gray-900 dark:text-white">
                    {transaction.price
                      ? formatCurrency(transaction.price)
                      : "-"}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white">
                    {transaction.amount > 0 ? "+" : ""}
                    {formatCurrency(transaction.amount)}
                  </td>
                  <td className="px-4 py-3 whitespace-nowrap text-sm text-right font-medium">
                    {transaction.action === "SELL" &&
                    transaction.realized_pnl !== null &&
                    transaction.realized_pnl !== undefined &&
                    transaction.realized_pnl !== "" ? (
                      <span
                        className={
                          parseFloat(transaction.realized_pnl) > 0
                            ? "text-green-600 dark:text-green-400"
                            : parseFloat(transaction.realized_pnl) < 0
                              ? "text-red-600 dark:text-red-400"
                              : "text-gray-900 dark:text-white"
                        }
                      >
                        {parseFloat(transaction.realized_pnl) > 0 ? "+" : ""}
                        {formatCurrency(transaction.realized_pnl)}
                      </span>
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">
                        -
                      </span>
                    )}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-gray-600 dark:text-gray-400">
            Page {page} of {totalPages}
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600 rounded-md hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActivityTable;
